import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { me } from '@/api';

const AuthCtx = createContext({ 
  user: null, 
  role: null,
  token: null,
  loading: true,
  login: () => {},
  logout: () => {},
  setUser: () => {}
});

export function AuthProvider({ children }) {
  // Token from sessionStorage (NOT localStorage - session only)
  const [token, setToken] = useState(() => sessionStorage.getItem('jc_token'));
  
  // Role is NEVER read from storage - only from /me API response
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Track previous identity for change detection
  const prevIdentity = useRef({ token: null, userId: null, role: null });
  
  // Flag to prevent double logout
  const isLoggingOut = useRef(false);
  
  // Destructive logout - clears EVERYTHING
  const logout = useCallback(() => {
    // Prevent multiple logout calls
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Preserve only app version
    localStorage.setItem("servicego_app_version", "2.0.0");
    
    // Reset state
    setToken(null);
    setRole(null);
    setUser(null);
    
    // Reset identity tracking
    prevIdentity.current = { token: null, userId: null, role: null };
    
    // Hard redirect to login
    window.location.replace("/login");
  }, []);
  
  // Login - saves token to sessionStorage and updates state
  const login = useCallback((newToken, userData) => {
    // Reset logout flag
    isLoggingOut.current = false;
    
    // Save to sessionStorage
    sessionStorage.setItem('jc_token', newToken);
    
    // Update state
    setToken(newToken);
    setUser(userData);
    setRole(userData?.role || null);
    
    // Update identity tracking with new user
    prevIdentity.current = { 
      token: newToken, 
      userId: userData?.id || null, 
      role: userData?.role || null 
    };
  }, []);
  
  // Fetch user on mount and when token changes
  useEffect(() => {
    // Skip if logging out
    if (isLoggingOut.current) return;
    
    if (!token) {
      setLoading(false);
      setUser(null);
      setRole(null);
      return;
    }
    
    setLoading(true);
    
    me()
      .then(res => {
        const userData = res.data;
        const newRole = userData.role;
        const newUserId = userData.id;
        
        // Check for identity change (different user logged in)
        // Only check if we had a previous identity AND it's different
        const prev = prevIdentity.current;
        if (prev.userId && prev.userId !== newUserId) {
          console.warn('ServiceGo: User ID changed, forcing logout');
          logout();
          return;
        }
        
        // Update identity tracking
        prevIdentity.current = { token, userId: newUserId, role: newRole };
        
        // Set state from API response ONLY
        setUser(userData);
        setRole(newRole);
      })
      .catch(err => {
        console.error('ServiceGo: /me failed, logging out', err);
        logout();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, logout]);
  
  return (
    <AuthCtx.Provider value={{ 
      user, 
      role,
      token,
      loading, 
      login,
      logout,
      setUser 
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
